import { Injectable } from '@angular/core';

import { HttpWrapper } from '../../../shared';
import moment from 'moment/moment';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';
import { CrewServiceSpa } from 'src/app/generated/services/transportation/crew/crewSpa';
import { Crew, CrewChange, CrewAppraisal, CrewChangeItem } from 'src/app/generated/services/transportation/crew/crew';

export enum CredentialItems {
  processingCertificateEndorsement = 'processingCertificateEndorsement',
  courseEndorsement = 'courseEndorsement',
  endorsementCertificateProficiency = 'endorsementCertificateProficiency',
  certificateProficiency = 'certificateProficiency',
  endorsementCertificateCompetence = 'endorsementCertificateCompetence',
  transitoryCertificate = 'transitoryCertificate',
  passport = 'passport',
  seamanBook = 'seamanBook'
}

export const CredentialItemsMap = new Map<string, string>([
  ['Passport', CredentialItems.passport],
  ['Seaman Book', CredentialItems.seamanBook],
  ['Processing Certificate Of Endorsement', CredentialItems.processingCertificateEndorsement],
  ['Course Endorsement', CredentialItems.courseEndorsement],
  ['Endorsement Of a Certificate Of Proficiency', CredentialItems.endorsementCertificateProficiency],
  ['Certificate Of Proficiency', CredentialItems.certificateProficiency],
  ['Endorsement Of a Certificate Of Competence', CredentialItems.endorsementCertificateCompetence],
  ['Transitory Certificate', CredentialItems.transitoryCertificate],

]);

export const CredentialItemsReversMap = new Map<string, string>([
  ['passport', 'Passport'],
  ['seamanBook', 'Seaman Book'],
  ['processingCertificateEndorsement', 'Processing Certificate Of Endorsement'],
  ['courseEndorsement', 'Course Endorsement'],
  ['endorsementCertificateProficiency', 'Endorsement Of a Certificate Of Proficiency'],
  ['certificateProficiency', 'Certificate Of Proficiency'],
  ['endorsementCertificateCompetence', 'Endorsement Of a Certificate Of Competence'],
  ['transitoryCertificate', 'Transitory Certificate'],
]);

@Injectable(
  {providedIn: 'root'}
)
export class CrewChangeServices {
  constructor(
    private _httpWrapper: HttpWrapper,
    private _crewService: CrewServiceSpa,
  ) { }

  getPlanningPhases() {
    return ['Incoming Flight', 'On Board', 'Off Board', 'Outgoing Flight'];
  }

  getExcelReport() {
    return this._httpWrapper.download('vessels/crew/excel');
  }

  getLastExcelReport() {
    return this._httpWrapper.download('vessels/crew/LastPlanExcel');
  }

  // Download excel file: crew list with all crew members and their last crew change plan
  downloadCrew() {
    return this._httpWrapper.download('vessels/crew/download');
  }

  calculateOnboardTime(crewChange: CrewChange) {
    if (crewChange) {
      const onboardItem = crewChange.item.find(c => c.plan === 'On Board');
      const offBoardItem = crewChange.item.find(c => c.plan === 'Off Board');
      if (onboardItem && !offBoardItem) {
        const today = new Date().getTime();
        if (onboardItem.time < today) {
          return moment().diff(moment(onboardItem.time), 'months', true).toFixed(2);
        } else {
          return 'On Board time is affter today';
        }
      }
      if (onboardItem && offBoardItem) {
        if (onboardItem.time < offBoardItem.time) {
          return moment(offBoardItem.time).diff(moment(onboardItem.time), 'months', true).toFixed(2);
        } else {
          return 'Off Board befre on Borad';
        }
      }
    }
    return '';
  }

  getCrewStatus(crew: Crew) {
    if (crew.crewChanges
      .filter(c => !c.isCancelled && !c.isDeleted &&
        !c.item.map(c1 => c1.plan).includes(this.getPlanningPhases()[3])
      ).length !== 0) {
      return 'On-board';
    }
    const currentTime = Date.now();
    if (crew.seamanDetails && crew.passportDetails && crew.seamanDetails.expirationDate &&
      (new Date(crew.seamanDetails.expirationDate).getTime() > currentTime) &&
      crew.passportDetails.expirationDate &&
      new Date(crew.passportDetails.expirationDate).getTime() > currentTime
    ) {
      return 'Rest';
    }
    return 'Incomplete';
  }

  findPlanAndOrganisation(crewChange: CrewChange, selectedPlan: string, organisationsMap: Map<string, Organisation>): [CrewChangeItem, Organisation] {
    const selectedPlanItem = crewChange?.item?.find(p => p.plan === selectedPlan);
    if (selectedPlanItem && organisationsMap) {
      const org = organisationsMap.get(selectedPlanItem.place);
      if (!org) {
        console.error(`Organisation not found for id ${selectedPlanItem.place}`);
        return [CrewChangeItem.create(), Organisation.create()];
      } else {
        return [selectedPlanItem, org];
      }
    } else {
      return [CrewChangeItem.create(), Organisation.create()];
    }
  }

  // planTime(crewChange: CrewChange, selectedPlan: string, organisationsMap: Map<string, Organisation>) {
  //   const [selectedPlanItem, org] = this.findPlanAndOrganisation(crewChange, selectedPlan, organisationsMap);
  //   if (selectedPlanItem && org) {
  //     return `${DateTimeHelpers.getDateTimeFromTimestamp(org.timezone, selectedPlanItem.time)}`;
  //   }
  // }
  planTime(crewChange: CrewChange, selectedPlan: string, organisationsMap: Map<string, Organisation>) {
    const [selectedPlanItem, org] = this.findPlanAndOrganisation(crewChange, selectedPlan, organisationsMap);
    if (selectedPlanItem && org && selectedPlanItem.time) {
      return DateTimeHelpers.getDateTimeFromTimestamp(org.timezone, selectedPlanItem.time);
    }
    return '';
  }

  planPlace(crewChange: CrewChange, selectedPlan: string, organisationsMap: Map<string, Organisation>) {
    const [selectedPlanItem, org] = this.findPlanAndOrganisation(crewChange, selectedPlan, organisationsMap);
    if (selectedPlanItem && org) {
      return org.nameTranslations['en'];
    }
  }

  onboardTimeMonthsDays(crewChange: CrewChange) {
    const firstItem = crewChange?.item?.find(p => p.plan === 'On Board');
    const secondItem = crewChange?.item?.find(p => p.plan === 'Off Board');
    if (firstItem) {
      return `${moment(secondItem?.time).diff(moment(firstItem.time), 'months', true).toFixed(2)}
      (${moment(secondItem?.time).diff(moment(firstItem.time), 'days', true).toFixed(2)} days)`;
    } else {
      return 0;
    }
  }

  travelTimeDays(crewChange: CrewChange) {
    const incoming = crewChange?.item?.find(p => p.plan === 'Incoming Flight');
    const onBoard = crewChange?.item?.find(p => p.plan === 'On Board');
    const offBoard = crewChange?.item?.find(p => p.plan === 'Off Board');
    const outgoing = crewChange?.item?.find(p => p.plan === 'Outgoing Flight');
    if (incoming && onBoard && offBoard && outgoing) {
      return (moment(onBoard.time).diff(moment(incoming.time), 'days', true) +
        moment(outgoing.time).diff(moment(offBoard.time), 'days', true)).toFixed(2);
    } else if (incoming && onBoard) {
      return moment(onBoard.time).diff(moment(incoming.time), 'days', true).toFixed(2);
    } else {
      return 0;
    }
  }

  getLastCrewChange(crewChanges: CrewChange[]) {
    if (crewChanges && crewChanges.length > 0) {
      const temp = crewChanges
        .filter(c => !c.isCancelled && !c.isDeleted)
        .sort((a, b) => Math.max(...b.item.map(i => i.time)) - Math.max(...a.item.map(i => i.time)));
      return temp[0];
    }
    return;
  }

  calculateRating(crewAppraisals: CrewAppraisal[]) {
    let average = 0;
    if (crewAppraisals && crewAppraisals.length > 0) {
      const rate = crewAppraisals
        .flatMap(appraisal => appraisal.appraisals)
        .filter(a => a.category !== 'Training')
        .map(a => a.grade)
        .map(i => {
          if (i === 'a') {
            return 100;
          } else if (i === 'b') {
            return 80;
          } else if (i === 'c') {
            return 60;
          } else if (i === 'd') {
            return 40;
          } else if (i === 'e') {
            return 20;
          }
        });
      average = (rate.reduce((a, b) => a + (b || 0), 0) / rate.length) / 20;
    }
    return [Array(Math.round(average)).fill(1), Array(Math.round(5 - average)).fill(1), Math.round(average * 10) / 10];
  }

  getLatestStatus(crew: Crew) {
    if (crew.remarks && crew.remarks.length > 0) {
      const re = crew.remarks.map(r =>
        ({
          ...r,
          date: (r.date) ? r.date : '0001-01-01'
        })
      );
      return re.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0].status;
    }
    return 'active';
  }

  getCrewActivationStatus(crew: Crew) {
    const latestStatus = this.getLatestStatus(crew);
    return (latestStatus === 'blacklisted') ? 'fa-regular fa-thumbs-down text-red' :
      (latestStatus === 'conditional') ? 'fa fa-exclamation text-warning' : 'fa-regular fa-thumbs-up text-green';
  }
}
